"use client"

import Image from "next/image"
import type { PortfolioProject } from "@/lib/portfolio-data"

export function ProjectCard({ project }: { project: PortfolioProject }) {
  return (
    <article
      id={project.slug}
      className="group relative border border-border/40 overflow-hidden transition-all duration-300 hover:border-accent/40"
    >
      <div className="flex flex-col md:flex-row">
        {/* Left: Screenshot */}
        <div className="relative w-full md:w-[45%] aspect-video md:aspect-auto md:min-h-[280px]">
          {project.screenshot ? (
            <Image
              src={project.screenshot}
              alt={`${project.title} screenshot`}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 45vw"
            />
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center bg-muted/30 border border-dashed border-border/60"
              aria-hidden
            >
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60">
                Screenshot coming soon
              </span>
            </div>
          )}
        </div>

        {/* Right: Content */}
        <div className="flex-1 flex flex-col p-6 md:p-8">
          <a
            href={`#${project.slug}`}
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent hover:underline"
          >
            {project.medium}
          </a>
          <h3 className="mt-2 font-[var(--font-bebas)] text-3xl md:text-4xl tracking-tight text-foreground">
            {project.title}
          </h3>
          <div className="mt-4 space-y-6">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Problem:</span>
              <p className="mt-1 font-sans text-sm text-foreground/90 leading-relaxed">
                {project.servicesOpted?.length > 0 
                  ? project.servicesOpted.join(" / ")
                  : "Local security and infrastructure challenges"
                }
              </p>
            </div>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Solution:</span>
              <p className="mt-1 font-sans text-sm text-foreground/90 leading-relaxed">
                {project.whatsBuilt}
              </p>
            </div>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Architecture:</span>
              <p className="mt-1 font-sans text-sm text-foreground/90 leading-relaxed">
                {project.statusInitiated} built with comprehensive security layers
              </p>
            </div>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Outcome:</span>
              <p className="mt-1 font-sans text-sm text-foreground/90 leading-relaxed">
                {project.completed} – {project.progress}% completed
              </p>
            </div>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Technologies:</span>
              <div className="mt-1 flex flex-wrap gap-2">
                {project.notesUpdates?.slice(0, 5).map((note, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-accent/10 border border-accent/20 rounded font-mono text-[9px] text-accent">
                    {note}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  )
}
